/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › At a glance (docs/CONTRACT-fleet-governance.md sections 9.1 and 12.3): eight tiles from
 * GET /v1/admin/governance/tiles, each with its change against the range of the same length just before. A tile with
 * audit rows behind it opens them; Bridge and receiver refusals are counted from the turn traces and the Bridge audit
 * emitter, which write no audit rows.
 */
import type { ReactNode } from 'react';
import {
  AUTO_RUN_CHOICE, ELEVATION_CHOICES, REFUSAL_KIND_EVENTS, changeText, durationText, pctText, trendsApi,
  type DrillChoice, type GovTiles, type Tile,
} from '../../lib/trends';
import { Grid, Kpi, Panel } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { SectionError, ShowRows, type SectionProps } from './parts';

const DLP_BLOCK_CHOICES: DrillChoice[] = [{ label: 'Outbound blocked', event: 'egress.dlp_blocked' }, { label: 'Tool write blocked', event: 'dlp.write_blocked' }];
const REFUSAL_CHOICES: DrillChoice[] = [...REFUSAL_KIND_EVENTS['tool-setting'], ...REFUSAL_KIND_EVENTS.rejected, ...REFUSAL_KIND_EVENTS.cap, ...REFUSAL_KIND_EVENTS['label-policy']];

export function TrendsTiles({ query, filter, onShowRows }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => trendsApi.tiles(query), `tiles|${JSON.stringify(query)}`);
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  const days = data?.range.days ?? filter.days ?? 30;

  const open = (title: string, choices: DrillChoice[]) => {
    if (!data) return;
    onShowRows({ title, choices, app: filter.app || null, range: { from: data.range.from, to: data.range.to } });
  };
  const tile = (label: string, value: (data: GovTiles) => Tile, show: (tile: Tile) => string, choices: DrillChoice[] | null): ReactNode => {
    const current = data ? value(data) : null;
    return (
      <Kpi
        key={label}
        label={label}
        loading={loading}
        value={current ? show(current) : '—'}
        sub={current ? (
          <span className="ui-cell-stack">
            <span>{changeText(current, days)}</span>
            {choices ? <span><ShowRows onClick={() => open(label, choices)} /></span> : null}
          </span>
        ) : undefined}
      />
    );
  };

  return (
    <Panel title="At a glance" description={data ? `${data.range.from} to ${data.range.to}, against ${data.previous.from} to ${data.previous.to}` : 'The range against the one just before it'}>
      {load.state === 'failed' ? <SectionError title="The tiles could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : (
        <Grid cols={4} label="Trends at a glance">
          {tile('DLP blocks', (d) => d.dlpBlocks, (t) => count(t.value), DLP_BLOCK_CHOICES)}
          {tile('Refusals', (d) => d.refusals, (t) => count(t.value), REFUSAL_CHOICES)}
          {tile('Auto-run share of writes', (d) => d.autoRunSharePct, (t) => pctText(t.value), [AUTO_RUN_CHOICE, { label: 'Confirmed by the person', event: 'action.requester_confirmed' }])}
          <Kpi
            label="Awaiting an approver now"
            loading={loading}
            value={data ? count(data.awaitingNow) : '—'}
            sub={data ? (
              <span className="ui-cell-stack">
                <span>cards sent to an approver within the last hour</span>
                <span><ShowRows onClick={() => open('Awaiting an approver now', [{ label: 'Sent to an approver', event: 'action.submitted_for_approval' }])} /></span>
              </span>
            ) : undefined}
          />
          {tile('Median time to approve', (d) => d.medianApproveSeconds, (t) => durationText(t.value), [{ label: 'Approved', event: 'action.approved' }, { label: 'Rejected', event: 'action.rejected' }])}
          {tile('Bridge and receiver refusals', (d) => d.bridgeRefusals, (t) => count(t.value), null)}
          {tile('Elevations granted', (d) => d.elevationsGranted, (t) => count(t.value), [ELEVATION_CHOICES[0]!])}
          {tile('Elevations denied', (d) => d.elevationsDenied, (t) => count(t.value), [ELEVATION_CHOICES[1]!])}
        </Grid>
      )}
    </Panel>
  );
}
